import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DNAConcentrationCalculator = () => {
  const [dnaData, setDnaData] = useState({
    absorbance260: '',
    absorbance280: '',
    absorbance230: '',
    dilutionFactor: '1',
    sampleType: 'dsDNA',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDnaData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setDnaData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateDNAConcentration = () => {
    const { absorbance260, absorbance280, absorbance230, dilutionFactor, sampleType } = dnaData;

    if (!absorbance260 || !dilutionFactor) {
      setError('Please fill in required fields');
      return;
    }

    try {
      const a260 = parseFloat(absorbance260);
      const a280 = absorbance280 ? parseFloat(absorbance280) : 0;
      const a230 = absorbance230 ? parseFloat(absorbance230) : 0;
      const dilution = parseFloat(dilutionFactor);

      if (isNaN(a260) || isNaN(dilution)) {
        setError('Please enter valid numbers for absorbance and dilution');
        return;
      }

      if (a260 <= 0 || dilution <= 0) {
        setError('Absorbance and dilution must be greater than zero');
        return;
      }

      const calculateConcentration = () => {
        const extinctionCoefficients = {
          'dsDNA': 50,
          'ssDNA': 33,
          'RNA': 40
        };

        const extinction = extinctionCoefficients[sampleType] || 50;
        return (a260 * extinction * dilution).toFixed(2);
      };

      const calculatePurityRatios = () => {
        const a260_a280 = a280 ? (a260 / a280).toFixed(2) : null;
        const a260_a230 = a230 ? (a260 / a230).toFixed(2) : null;
        return {
          a260_a280,
          a260_a230
        };
      };

      const generateChart = () => {
        const wavelengths = [230, 260, 280];
        const absorbances = [a230, a260, a280];

        return {
          labels: wavelengths,
          datasets: [
            {
              label: 'Absorbance Measurements',
              data: absorbances,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1,
              fill: false
            }
          ]
        };
      };

      const resultsData = {
        absorbance260: a260,
        absorbance280: a280,
        absorbance230: a230,
        dilutionFactor: dilution,
        sampleType,
        concentration: calculateConcentration(),
        ...calculatePurityRatios(),
        qualityAssessment: getQualityAssessment(calculatePurityRatios()),
        optimizationTips: getOptimizationTips(dnaData)
      };

      setResults(resultsData);
      setChartData(generateChart());

    } catch (error) {
      setError('An error occurred while calculating DNA concentration');
    }
  };

  const getQualityAssessment = (ratios) => {
    const { a260_a280, a260_a230 } = ratios;
    const assessment = [];

    if (a260_a280) {
      if (a260_a280 >= 1.8 && a260_a280 <= 2.0) {
        assessment.push('High purity DNA (protein contamination is minimal)');
      } else if (a260_a280 < 1.8) {
        assessment.push('Potential protein contamination (ratio < 1.8)');
      } else if (a260_a280 > 2.0) {
        assessment.push('Potential RNA contamination (ratio > 2.0)');
      }
    }

    if (a260_a230) {
      if (a260_a230 >= 2.0) {
        assessment.push('High purity DNA (organic contamination is minimal)');
      } else if (a260_a230 < 2.0) {
        assessment.push('Potential organic compound contamination (ratio < 2.0)');
      }
    }

    return assessment;
  };

  const getOptimizationTips = (dnaData) => {
    const { sampleType } = dnaData;
    const tips = [];

    if (sampleType === 'dsDNA') {
      tips.push(
        'Use RNase-free water for dilutions',
        'Store at -20°C to prevent degradation',
        'Avoid multiple freeze-thaw cycles'
      );
    } else if (sampleType === 'ssDNA') {
      tips.push(
        'Handle with care to prevent denaturation',
        'Use appropriate buffers for stability',
        'Monitor for secondary structure formation'
      );
    } else if (sampleType === 'RNA') {
      tips.push(
        'Maintain RNase-free conditions',
        'Use DEPC-treated water',
        'Store at -80°C for long-term stability'
      );
    }

    tips.push(
      'Use proper controls for absorbance measurements',
      'Check for particulate matter in samples',
      'Ensure accurate dilution factors',
      'Monitor pH for optimal readings',
      'Document all measurements for reproducibility'
    );

    return tips;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>DNA Concentration Calculator | Molecular Biology Tool</title>
          <meta name="description" content="Calculate DNA concentration and purity ratios with our advanced calculator. Analyze A260, A280, and A230 absorbance readings for DNA quality assessment." />
          <meta name="keywords" content="DNA concentration calculator, absorbance calculator, DNA purity analysis, molecular biology calculator, A260 A280 ratio" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8 max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🧬</div>
            <h1 className="text-3xl font-bold text-gray-800">DNA Concentration Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The DNA Concentration Calculator helps you analyze DNA samples and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Concentration calculations</li>
              <li>Purity ratio analysis</li>
              <li>Quality assessment</li>
              <li>Optimization recommendations</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Absorbance at 260 nm (A260):</label>
              <input
                type="number"
                name="absorbance260"
                value={dnaData.absorbance260}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter A260 value"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Absorbance at 280 nm (A280):</label>
              <input
                type="number"
                name="absorbance280"
                value={dnaData.absorbance280}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter A280 value (optional)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Absorbance at 230 nm (A230):</label>
              <input
                type="number"
                name="absorbance230"
                value={dnaData.absorbance230}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter A230 value (optional)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Dilution Factor:</label>
              <input
                type="number"
                name="dilutionFactor"
                value={dnaData.dilutionFactor}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter dilution factor"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sample Type:</label>
              <select
                name="sampleType"
                value={dnaData.sampleType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="dsDNA">Double-stranded DNA (dsDNA)</option>
                <option value="ssDNA">Single-stranded DNA (ssDNA)</option>
                <option value="RNA">RNA</option>
              </select>
            </div>

            <div className="input-group">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={dnaData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-pink-600 mr-2"
                />
                <span className="text-gray-700">Show detailed quality analysis and optimization tips</span>
              </label>
            </div>
          </div>

          <button
            onClick={calculateDNAConcentration}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate DNA Concentration
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">DNA Sample Analysis</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Concentration</h3>
                  <p className="text-xl font-bold">{results.concentration} ng/µL</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Purity Ratios</h3>
                  <p>A260/A280: {results.a260_a280}</p>
                  <p>A260/A230: {results.a260_a230}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Quality Assessment</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    {results.qualityAssessment.map((assessment, index) => (
                      <li key={index} className="text-gray-600">{assessment}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Absorbance'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Wavelength (nm)'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Absorbance Spectrum'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Optimization Tips</h3>
                <ul className="list-disc pl-6 space-y-2">
                  {results.optimizationTips.map((tip, index) => (
                    <li key={index} className="text-gray-600">{tip}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Understanding DNA Concentration Section */}
        <div className="calculator-section mt-12 ">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding DNA Concentration <span className="text-blue-600">📊</span></h2>
          <div className="text-gray-600">
            <p>DNA concentration is a crucial metric in molecular biology that helps in:</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Sample Preparation: Ensuring proper DNA quantity for experiments</li>
              <li>Quality Control: Monitoring sample purity and integrity</li>
              <li>Experiment Design: Planning for PCR, sequencing, and cloning</li>
              <li>Data Interpretation: Validating experimental results</li>
            </ul>
            <p className="italic mt-4">Note: Ideal A260/A280 ratio is 1.8-2.0 for pure DNA</p>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12 ">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use DNA Concentration Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the DNA Concentration Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Prepare Your Sample</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Measure absorbance at 260 nm (A260)</li>
                <li>Measure absorbance at 280 nm (A280) for purity</li>
                <li>Measure absorbance at 230 nm (A230) for contamination</li>
                <li>Note the dilution factor if sample was diluted</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input A260 value (required)</li>
                <li>Enter A280 and A230 values (optional but recommended)</li>
                <li>Specify dilution factor</li>
                <li>Select sample type (dsDNA, ssDNA, or RNA)</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Review DNA concentration in ng/µL</li>
                <li>Check purity ratios (A260/A280 and A260/A230)</li>
                <li>Review quality assessment</li>
                <li>Follow optimization tips for improvement</li>
              </ul>
            </div>

            <div className="step-section mt-6 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Use proper controls for absorbance measurements</li>
                <li>Check for particulate matter in samples</li>
                <li>Ensure accurate dilution factors</li>
                <li>Monitor pH for optimal readings</li>
                <li>Document all measurements for reproducibility</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DNAConcentrationCalculator;